/**
 * HangarX Growth OS SDK
 * Simple, lightweight SDK for event tracking and growth intelligence
 */

import { Destination, GA4Destination, MixpanelDestination, SegmentDestination, GA4Config, MixpanelConfig, SegmentConfig } from './destinations'

export interface TinybirdConfig {
  enabled: boolean
  token: string
  datasource?: string
  endpoint?: string
}

export type StorageStrategy = 'hangarx-only' | 'tinybird-only' | 'dual' | 'tinybird-primary'

export interface DestinationConfig {
  type: 'ga4' | 'mixpanel' | 'segment' | 'custom'
  enabled?: boolean
  config: GA4Config | MixpanelConfig | SegmentConfig | any
}

export interface HangarXConfig {
  apiKey: string
  apiEndpoint?: string
  debug?: boolean
  autoTrack?: boolean
  batchSize?: number
  flushInterval?: number
  
  // Tinybird integration
  tinybird?: TinybirdConfig
  storageStrategy?: StorageStrategy
  
  // Multi-destination support
  destinations?: DestinationConfig[]
}

export interface EventData {
  event_type: string
  user_id?: string
  anonymous_id?: string
  session_id?: string
  properties?: Record<string, any>
  context?: Record<string, any>
}

export interface UserTraits {
  [key: string]: any
}

export class HangarX {
  private config: HangarXConfig
  private eventQueue: EventData[] = []
  private sessionId: string
  private anonymousId: string
  private userId?: string
  private flushTimer?: NodeJS.Timeout
  private destinations: Destination[] = []

  constructor(config: HangarXConfig) {
    this.config = {
      apiEndpoint: 'https://ingest.hangarx.ai',
      debug: false,
      autoTrack: true,
      batchSize: 20,
      flushInterval: 10000, // 10 seconds
      ...config
    }

    // Generate session and anonymous IDs
    this.sessionId = this.generateId('sess_')
    this.anonymousId = this.getOrCreateAnonymousId()

    // Auto-track page views if enabled
    if (this.config.autoTrack && typeof window !== 'undefined') {
      this.setupAutoTracking()
    }

    // Initialize destinations
    this.initializeDestinations()
    
    // Start flush timer
    this.startFlushTimer()

    this.log('HangarX SDK initialized', { config: this.config })
  }

  /**
   * Track a custom event
   */
  async track(eventType: string, properties?: Record<string, any>): Promise<void> {
    // Validate event
    if (!eventType || typeof eventType !== 'string') {
      this.log('Invalid event: event_type must be a non-empty string', { eventType })
      return
    }

    const event: EventData = {
      event_type: eventType,
      user_id: this.userId,
      anonymous_id: this.anonymousId,
      session_id: this.sessionId,
      properties: properties || {},
      context: this.getContext()
    }

    this.enqueueEvent(event)
  }

  /**
   * Identify a user
   */
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    this.userId = userId
    
    // Send identify to destinations
    await Promise.allSettled(
      this.destinations.map(dest => 
        dest.identify ? dest.identify(userId, traits) : Promise.resolve()
      )
    )
    
    await this.track('user_identified', {
      user_id: userId,
      traits: traits || {},
      previous_anonymous_id: this.anonymousId
    })
  }

  /**
   * Track page view (automatically called if autoTrack is enabled)
   */
  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    const pageProperties = {
      page_name: name || document.title,
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer,
      ...properties
    }
    
    // Send page to destinations
    await Promise.allSettled(
      this.destinations.map(dest => 
        dest.page ? dest.page(name, properties) : Promise.resolve()
      )
    )

    await this.track('page_view', pageProperties)
  }

  /**
   * Track screen view (for mobile apps or SPAs)
   */
  async screen(name: string, properties?: Record<string, any>): Promise<void> {
    const screenProperties = {
      screen_name: name,
      screen_width: typeof window !== 'undefined' ? window.innerWidth : undefined,
      screen_height: typeof window !== 'undefined' ? window.innerHeight : undefined,
      ...properties
    }
    
    await this.track('screen_view', screenProperties)
  }

  /**
   * Track conversion events
   */
  async conversion(conversionType: string, value?: number, properties?: Record<string, any>): Promise<void> {
    await this.track('conversion', {
      conversion_type: conversionType,
      conversion_value: value,
      ...properties
    })
  }

  /**
   * Track campaign interactions
   */
  async campaign(campaignId: string, action: string, properties?: Record<string, any>): Promise<void> {
    await this.track('campaign_interaction', {
      campaign_id: campaignId,
      action,
      ...properties
    })
  }

  /**
   * Reset user data and start new session
   * Call this when user logs out
   */
  reset(): void {
    // Clear user ID
    this.userId = undefined
    
    // Generate new session ID
    this.sessionId = this.generateId('sess_')
    
    // Flush remaining events before reset
    this.flush().catch(() => {
      // Ignore flush errors during reset
    })
    
    this.log('User session reset', {
      newSessionId: this.sessionId,
      anonymousId: this.anonymousId
    })
  }

  /**
   * Flush events immediately
   */
  async flush(): Promise<void> {
    if (this.eventQueue.length === 0) return

    const events = [...this.eventQueue]
    this.eventQueue = []

    const strategy = this.config.storageStrategy || 'hangarx-only'
    const promises: Promise<any>[] = []

    try {
      // Send to additional destinations
      if (this.destinations.length > 0) {
        promises.push(
          ...this.destinations.map(dest =>
            dest.send(events).catch(err => {
              this.log(`Destination ${dest.name} failed`, { error: err })
            })
          )
        )
      }

      // Strategy: Dual storage (send to both)
      if (strategy === 'dual') {
        promises.push(
          this.sendToTinybird(events).catch(err => {
            this.log('Tinybird failed, event still sent to HangarX', { error: err })
          })
        )
        promises.push(
          this.sendToHangarX(events).catch(err => {
            this.log('HangarX failed, event still sent to Tinybird', { error: err })
          })
        )
        await Promise.allSettled(promises)
      }
      // Strategy: Tinybird primary, HangarX fallback
      else if (strategy === 'tinybird-primary') {
        try {
          await this.sendToTinybird(events)
          this.log('Events sent to Tinybird (primary)', { count: events.length })
        } catch (error) {
          this.log('Tinybird failed, falling back to HangarX', { error })
          await this.sendToHangarX(events)
        }
        await Promise.allSettled(promises)
      }
      // Strategy: Tinybird only
      else if (strategy === 'tinybird-only') {
        await this.sendToTinybird(events)
        await Promise.allSettled(promises)
        this.log('Events sent to Tinybird only', { count: events.length })
      }
      // Strategy: HangarX only (default)
      else {
        await this.sendToHangarX(events)
        await Promise.allSettled(promises)
        this.log('Events sent to HangarX only', { count: events.length })
      }
    } catch (error) {
      // Re-queue events on failure
      this.eventQueue.unshift(...events)
      this.log('Failed to flush events', { error, count: events.length })
      throw error
    }
  }

  /**
   * Get growth insights for the current user/session
   */
  async getInsights(): Promise<any> {
    if (!this.config.apiEndpoint) {
      throw new Error('API endpoint is not configured.')
    }
    try {
      const response = await fetch(
        `${this.config.apiEndpoint.replace(':3003', ':3001')}/insights/business-pulse`,
        {
          headers: {
            'X-API-Key': this.config.apiKey || ''
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      this.log('Failed to get insights', { error })
      throw error
    }
  }

  /**
   * Get personalized recommendations
   */
  async getRecommendations(): Promise<any> {
    if (!this.config.apiEndpoint) {
      throw new Error('API endpoint is not configured.')
    }
    try {
      const response = await fetch(
        `${this.config.apiEndpoint.replace(':3003', ':3001')}/recommendations`,
        {
          headers: {
            'X-API-Key': this.config.apiKey || ''
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      return response.json()
    } catch (error) {
      this.log('Failed to get recommendations', { error })
      throw error
    }
  }

  // Private methods

  private enqueueEvent(event: EventData): void {
    this.eventQueue.push(event)
    this.log('Event enqueued', { event, queueSize: this.eventQueue.length })

    // Flush if batch size reached
    if (this.eventQueue.length >= this.config.batchSize!) {
      this.flush().catch(error => {
        this.log('Auto-flush failed', { error })
      })
    }
  }

  private setupAutoTracking(): void {
    // Track initial page view
    this.page()

    // Track page changes (for SPAs)
    let currentPath = window.location.pathname
    const checkPathChange = () => {
      if (window.location.pathname !== currentPath) {
        currentPath = window.location.pathname
        this.page()
      }
    }

    // Listen for navigation events
    window.addEventListener('popstate', checkPathChange)
    
    // Override pushState and replaceState for SPA navigation
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState

    history.pushState = function(...args) {
      originalPushState.apply(history, args)
      setTimeout(checkPathChange, 0)
    }

    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args)
      setTimeout(checkPathChange, 0)
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.flush().catch(error => {
          this.log('Scheduled flush failed', { error })
        })
      }
    }, this.config.flushInterval!)
  }

  private getContext(): Record<string, any> {
    const context: Record<string, any> = {
      timestamp: new Date().toISOString(),
      
      // Library metadata
      library: {
        name: '@hangarx/browser',
        version: '2.0.0'
      }
    }

    if (typeof window !== 'undefined') {
      context.url = window.location.href
      context.referrer = document.referrer
      context.user_agent = navigator.userAgent
      context.screen_width = screen.width
      context.screen_height = screen.height
      
      // Extract UTM parameters
      const urlParams = new URLSearchParams(window.location.search)
      const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
      utmParams.forEach(param => {
        const value = urlParams.get(param)
        if (value) context[param] = value
      })
    }

    return context
  }

  private getOrCreateAnonymousId(): string {
    const key = 'hangarx_anonymous_id'
    
    if (typeof localStorage !== 'undefined') {
      let id = localStorage.getItem(key)
      if (!id) {
        id = this.generateId('anon_')
        localStorage.setItem(key, id)
      }
      return id
    }
    
    return this.generateId('anon_')
  }

  private generateId(prefix: string = ''): string {
    return prefix + Date.now().toString(36) + Math.random().toString(36).substr(2, 9)
  }

  private generateEventId(): string {
    // Extract project ID from API key (format: proj_xxx)
    const projectId = this.extractProjectId(this.config.apiKey)
    const timestamp = Date.now()
    const random = Math.random().toString(36).substr(2, 8)
    
    // Format: projectId_timestamp_random
    return `${projectId}_${timestamp}_${random}`
  }

  private extractProjectId(apiKey: string): string {
    const match = apiKey.match(/^proj_([a-zA-Z0-9]+)/)
    return match ? match[1] : 'unknown'
  }

  private async sendToHangarX(events: EventData[]): Promise<void> {
    const response = await fetch(`${this.config.apiEndpoint}/events/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': this.config.apiKey || ''
      },
      body: JSON.stringify({ events })
    })

    if (!response.ok) {
      throw new Error(`HangarX HTTP ${response.status}: ${response.statusText}`)
    }
  }

  private async sendToTinybird(events: EventData[]): Promise<void> {
    if (!this.config.tinybird?.enabled || !this.config.tinybird?.token) {
      this.log('Tinybird not configured, skipping')
      return
    }

    const tinybirdEndpoint = this.config.tinybird.endpoint || this.detectTinybirdEndpoint()
    const datasource = this.config.tinybird.datasource || 'analytics_events_raw'

    // Transform events to Tinybird format
    const tinybirdEvents = events.map(event => ({
      event_id: this.generateEventId(),
      company_id: this.extractProjectId(this.config.apiKey),
      type: 'track',
      user_id: event.user_id || '',
      anonymous_id: event.anonymous_id || this.anonymousId,
      session_id: event.session_id || this.sessionId,
      event_name: event.event_type,
      properties: JSON.stringify(event.properties || {}),
      context: JSON.stringify(event.context || {}),
      timestamp: event.context?.timestamp || new Date().toISOString(),
      received_at: new Date().toISOString(),
      processed: 'false'
    }))

    const response = await fetch(`${tinybirdEndpoint}/v0/events?name=${datasource}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.config.tinybird.token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(tinybirdEvents)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Tinybird HTTP ${response.status}: ${errorText}`)
    }
  }

  private detectTinybirdEndpoint(): string {
    // Decode JWT token to get host
    try {
      const token = this.config.tinybird?.token || ''
      const payload = JSON.parse(atob(token.split('.')[1]))

      const hostMap: Record<string, string> = {
        'us_east': 'https://api.us-east.tinybird.co',
        'us_west': 'https://api.us-west.tinybird.co',
        'eu_west': 'https://api.eu-west.tinybird.co',
      }

      return hostMap[payload.host] || 'https://api.tinybird.co'
    } catch (error) {
      // Fallback to global endpoint
      return 'https://api.tinybird.co'
    }
  }

  private initializeDestinations(): void {
    if (!this.config.destinations || this.config.destinations.length === 0) {
      return
    }

    for (const destConfig of this.config.destinations) {
      if (destConfig.enabled === false) {
        continue
      }

      try {
        let destination: Destination | null = null

        switch (destConfig.type) {
          case 'ga4':
            destination = new GA4Destination(destConfig.config as GA4Config, this.config.debug)
            break
          case 'mixpanel':
            destination = new MixpanelDestination(destConfig.config as MixpanelConfig, this.config.debug)
            break
          case 'segment':
            destination = new SegmentDestination(destConfig.config as SegmentConfig, this.config.debug)
            break
          case 'custom':
            // Allow custom destinations
            if (destConfig.config && typeof destConfig.config.send === 'function') {
              destination = destConfig.config as Destination
            }
            break
        }

        if (destination) {
          this.destinations.push(destination)
          this.log(`Initialized destination: ${destination.name}`)
        }
      } catch (error) {
        this.log(`Failed to initialize destination: ${destConfig.type}`, { error })
      }
    }
  }

  private log(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[HangarX] ${message}`, data)
    }
  }

  // Cleanup
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
    }
    
    // Flush remaining events
    this.flush().catch(() => {
      // Ignore errors during cleanup
    })
  }
}
